import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Clock, CheckCircle2, Gem } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_CONTENT = {
  brand_line: "Wholesale fine jewellery, priced with total transparency.",
  subtitle: "Access itemised gold, diamond and colour-stone pricing built for jewellery retailers.",
  hero_image: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=900&q=80",
  footer_line: "Trusted B2B partner · Certified stones · Hallmarked gold",
  eyebrow: "Partner Access",
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginShop, refreshCart, refreshWishlist, shopLoggedIn } = useAuth();
  const [loginForm, setLoginForm] = useState({ shop_name: "", phone: "" });
  const [reqForm, setReqForm] = useState({ shop_name: "", phone: "", contact_person: "", city: "", email: "" });
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(FALLBACK_CONTENT);

  // Already logged in (e.g. navigated back here manually) — skip the form and
  // go straight to where the guard originally redirected the user from.
  useEffect(() => {
    if (shopLoggedIn) navigate(location.state?.from || "/", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopLoggedIn]);

  useEffect(() => {
    api.get("/login-page/public").then(({ data }) => setContent({ ...FALLBACK_CONTENT, ...data })).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/shops/login", loginForm);
      loginShop(null, data.shop);
      await Promise.all([refreshCart(), refreshWishlist()]);
      toast.success(`Welcome, ${data.shop.shop_name}`);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/shops/request", reqForm);
      setPending(data.status);
      toast.success("Request submitted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Brand side */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 hero-silk">
        <Link to="/" className="font-display text-2xl text-[hsl(var(--primary))]">MAAHEY<span className="text-[hsl(var(--gold-dark))]">JEWELS</span></Link>
        <div className="relative z-10">
          <img src="https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=900&q=80" alt="jewellery" className="rounded-2xl w-full h-72 object-cover card-shadow-soft" />
          <h1 className="font-display text-4xl mt-8 leading-tight">Wholesale fine jewellery, priced with total transparency.</h1>
          <p className="text-muted-foreground mt-3 max-w-md">Access itemised gold, diamond and colour-stone pricing built for jewellery retailers.</p>
        </div>
        <span className="text-xs text-muted-foreground">Trusted B2B partner · Certified stones · Hallmarked gold</span>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden font-display text-2xl text-[hsl(var(--primary))] mb-6">MAAHEY<span className="text-[hsl(var(--gold-dark))]">JEWELS</span></div>
          <div className="flex items-center gap-2 mb-2 text-[hsl(var(--primary))]"><Gem className="h-5 w-5" /><span className="text-sm uppercase tracking-widest">Partner Access</span></div>
          <h2 className="font-display text-3xl mb-6">Welcome</h2>

          {pending ? (
            <Alert data-testid="shop-login-pending-status" className="bg-[#FBF1DF] border-[#E7D0A8]">
              <Clock className="h-4 w-4 text-[#7A4B12]" />
              <AlertTitle className="text-[#7A4B12]">Request pending approval</AlertTitle>
              <AlertDescription className="text-[#7A4B12]">
                Thank you! Your shop is awaiting admin approval. You will be able to log in with your shop name & phone once approved (usually within a few hours).
              </AlertDescription>
            </Alert>
          ) : (
            <Tabs defaultValue="login">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger data-testid="tab-login" value="login">Login</TabsTrigger>
                <TabsTrigger data-testid="tab-request" value="request">Request Access</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <Card className="p-6 mt-4">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="ls-name">Shop Name</Label>
                      <Input id="ls-name" data-testid="shop-login-shop-name-input" required value={loginForm.shop_name} onChange={(e) => setLoginForm({ ...loginForm, shop_name: e.target.value })} placeholder="e.g. Demo Jewellers" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="ls-phone">Phone Number</Label>
                      <Input id="ls-phone" type="tel" inputMode="numeric" data-testid="shop-login-phone-input" required value={loginForm.phone} onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })} placeholder="10-digit shop phone number" className="mt-1" />
                    </div>
                    <Button data-testid="shop-login-submit-button" type="submit" disabled={loading} className="w-full">{loading ? "Signing in..." : "Login"}</Button>
                  </form>
                </Card>
              </TabsContent>

              <TabsContent value="request">
                <Card className="p-6 mt-4">
                  <form onSubmit={handleRequest} className="space-y-3">
                    <div>
                      <Label htmlFor="rq-name">Shop Name *</Label>
                      <Input id="rq-name" data-testid="shop-request-shop-name-input" required value={reqForm.shop_name} onChange={(e) => setReqForm({ ...reqForm, shop_name: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="rq-phone">Phone Number *</Label>
                      <Input id="rq-phone" type="tel" inputMode="numeric" data-testid="shop-request-phone-input" required value={reqForm.phone} onChange={(e) => setReqForm({ ...reqForm, phone: e.target.value })} placeholder="10-digit shop phone number" className="mt-1" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label htmlFor="rq-contact">Contact Person</Label><Input id="rq-contact" value={reqForm.contact_person} onChange={(e) => setReqForm({ ...reqForm, contact_person: e.target.value })} className="mt-1" /></div>
                      <div><Label htmlFor="rq-city">City</Label><Input id="rq-city" value={reqForm.city} onChange={(e) => setReqForm({ ...reqForm, city: e.target.value })} className="mt-1" /></div>
                    </div>
                    <div><Label htmlFor="rq-email">Email</Label><Input id="rq-email" type="email" value={reqForm.email} onChange={(e) => setReqForm({ ...reqForm, email: e.target.value })} className="mt-1" /></div>
                    <Button data-testid="shop-request-submit-button" type="submit" disabled={loading} className="w-full">{loading ? "Submitting..." : "Submit for Approval"}</Button>
                  </form>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
    </div>
  );
}
